import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
  Typography,
  InputAdornment
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { ManagementCompany } from '../../resources/types/managementCompaniesTypes';
import {
  createManagementCompanyAsync,
  updateManagementCompanyAsync
} from '../../redux/slices/managementCompaniesSlice';

interface Props {
  open: boolean;
  onClose: () => void;
  company: ManagementCompany | null;
}

const ManagementCompanyModal: React.FC<Props> = ({ open, onClose, company }) => {
  const dispatch = useDispatch();
  const [name, setName] = useState('');
  const [commissionPercent, setCommissionPercent] = useState('');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; commissionPercent?: string }>({});

  useEffect(() => {
    if (open) {
      if (company) {
        setName(company.name);
        setCommissionPercent(String(company.commissionPercent));
      } else {
        setName('');
        setCommissionPercent('');
      }
      setErrors({});
    }
  }, [open, company]);

  const validate = useCallback(() => {
    const newErrors: { name?: string; commissionPercent?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Назва обов\'язкова';
    }
    
    const percent = parseFloat(commissionPercent);
    if (isNaN(percent) || percent < 0 || percent > 100) {
      newErrors.commissionPercent = 'Введіть відсоток від 0 до 100';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name, commissionPercent]);

  const handleSave = useCallback(() => {
    if (!validate()) return;

    setSaving(true);

    const data = {
      name: name.trim(),
      commissionPercent: commissionPercent
    };

    if (company) {
      dispatch(updateManagementCompanyAsync(company._id, data, () => {
        setSaving(false);
        onClose();
      }));
    } else {
      dispatch(createManagementCompanyAsync(data, () => {
        setSaving(false);
        onClose();
      }));
    }
  }, [validate, name, commissionPercent, company, dispatch, onClose]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              backgroundColor: 'rgba(78, 205, 196, 0.15)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#4ECDC4'
            }}
          >
            🏢
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {company ? 'Редагувати компанію' : 'Нова компанія'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <TextField
            label="Назва компанії"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            error={!!errors.name}
            helperText={errors.name}
          />

          <TextField
            label="Комісія"
            value={commissionPercent}
            onChange={(e) => setCommissionPercent(e.target.value)}
            fullWidth
            required
            type="number"
            error={!!errors.commissionPercent}
            helperText={errors.commissionPercent || 'Відсоток комісії за замовчуванням для парковок цієї компанії'}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>
            }}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} color="inherit">
          Скасувати
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving}
          sx={{
            backgroundColor: '#4ECDC4',
            '&:hover': { backgroundColor: '#3BA99F' }
          }}
        >
          {saving ? 'Збереження...' : 'Зберегти'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ManagementCompanyModal;
